import React, { useCallback, useEffect, useMemo, useRef } from 'react';

export type DualRangeSliderProps = {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (next: [number, number]) => void;
  className?: string;
};

// Accessible dual-thumb range slider built with two inputs and a visual track
const DualRangeSlider: React.FC<DualRangeSliderProps> = ({
  min,
  max,
  step = 10,
  value,
  onChange,
  className = '',
}) => {
  const [minVal, maxVal] = value;
  const rangeRef = useRef<HTMLDivElement | null>(null);

  const percentFromValue = useCallback(
    (val: number) => Math.round(((val - min) / (max - min)) * 100),
    [min, max]
  );

  const left = useMemo(() => percentFromValue(minVal), [minVal, percentFromValue]);
  const right = useMemo(() => 100 - percentFromValue(maxVal), [maxVal, percentFromValue]);

  useEffect(() => {
    if (!rangeRef.current) return;
    rangeRef.current.style.left = `${left}%`;
    rangeRef.current.style.right = `${right}%`;
  }, [left, right]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Math.min(Number(e.target.value), maxVal - step);
    onChange([next, maxVal]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Math.max(Number(e.target.value), minVal + step);
    onChange([minVal, next]);
  };

  return (
    <div className={`relative w-full ${className}`}>
      {/* Track */}
      <div className="relative h-0.75 rounded-full bg-[#E5EEF2]">
        <div
          ref={rangeRef}
          className="absolute h-0.75 rounded-full bg-[#1B365E]"
          style={{ left: `${left}%`, right: `${right}%` }}
        />
      </div>

      {/* Inputs */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={minVal}
        onChange={handleMinChange}
        className="pointer-events-none absolute -top-2.25 h-5 w-full appearance-none bg-transparent outline-none z-30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#1B365E] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-runnable-track]:appearance-none [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:height-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:bg-[#1B365E] [&::-moz-range-thumb]:rounded-full [&::-moz-range-track]:appearance-none"
        aria-label="Valeur minimale"
      />
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={maxVal}
        onChange={handleMaxChange}
        className="pointer-events-none absolute -top-2.25 h-5 w-full appearance-none bg-transparent outline-none z-40 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#1B365E] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-runnable-track]:appearance-none [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:height-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:bg-[#1B365E] [&::-moz-range-thumb]:rounded-full [&::-moz-range-track]:appearance-none"
        aria-label="Valeur maximale"
      />
    </div>
  );
};

export default DualRangeSlider;
